/**
 * components/BetaApplication/steps/ApplicationReviewStep.tsx
 * Application Review Step - Final step of Beta Application
 *
 * Displays comprehensive review of all collected data:
 * - Account information
 * - Trading profile details
 * - Beta commitment responses
 */

// 1. External dependencies
import React from 'react';

// 2. Internal absolute imports
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Brand } from '@enfomentis/shared/config';
import { useBetaApplicationData } from '../hooks/useBetaApplicationData';

/**
 * Application Review Step Component
 *
 * Final step showing comprehensive review of all application data
 * before submission. Users can navigate back to edit any section.
 */
export const ApplicationReviewStep: React.FC = () => {
  const { data: draft } = useBetaApplicationData();

  // Helper function to format trading sessions for display
  const formatTradingSessions = (sessions: string[] | string, otherSpecification?: string): string => {
    const sessionDisplayMap: Record<string, string> = {
      'asian': 'Asian Session',
      'london': 'London Session',
      'ny-am': 'New York AM Session',
      'ny-pm': 'New York PM Session'
    };

    // Handle case where sessions might be a string instead of array
    let sessionsArray: string[] = [];
    if (Array.isArray(sessions)) {
      sessionsArray = sessions;
    } else if (typeof sessions === 'string') {
      // If it's a string, try to parse it or treat as single value
      sessionsArray = sessions.includes(',') ? sessions.split(',').map(s => s.trim()) : [sessions];
    }

    const displayNames = sessionsArray
      .filter(session => session !== 'other' && session.length > 1) // Filter out single characters
      .map(session => sessionDisplayMap[session] || session);

    if (sessionsArray.includes('other') && otherSpecification) {
      displayNames.push(otherSpecification);
    }

    return displayNames.join(', ');
  };

  if (!draft) {
    return <div>Loading...</div>;
  }

  return (
    <Card
      radius='none'
      shadow='none'
      className="max-w-4xl mx-auto"
    >
      <CardHeader>
        <div>
          <h2 className="text-2xl font-semibold mb-2 text-text-primary">Application Review</h2>
          <p className="text-text-secondary">Please review your complete application before submitting</p>
        </div>
      </CardHeader>

      <CardBody className="space-y-6">
        {/* Account Information Review */}
        <Card shadow="sm" className="border border-primary">
          <CardHeader className="bg-primary">
            <h3 className="text-lg font-semibold text-text-inverse">Account Information</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-text-secondary">Email:</span>
                <p className="text-text-primary">{draft.account.email}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-text-secondary">Full Name:</span>
                <p className="text-text-primary">{draft.account.fullName}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-text-secondary">Discord Username:</span>
                <p className="text-text-primary">{draft.account.discordUsername}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Trading Profile Review */}
        <Card shadow="sm" className="border border-success">
          <CardHeader className="bg-success">
            <h3 className="text-lg font-semibold text-text-inverse">Trading Profile</h3>
          </CardHeader>
          <CardBody>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <span className="text-sm font-medium text-text-secondary">Primary Assets:</span>
                <p className="text-text-primary capitalize">{draft.trading.primaryFocus.join(', ')}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-text-secondary">Experience Level:</span>
                <p className="text-text-primary capitalize">{draft.trading.experienceLevel.replace(/-/g, ' ')}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-text-secondary">Trading Style:</span>
                <p className="text-text-primary capitalize">
                  {draft.trading.tradingStyle === 'other'
                    ? draft.trading.tradingStyleOther
                    : draft.trading.tradingStyle.replace(/-/g, ' ')
                  }
                </p>
              </div>
              <div>
                <span className="text-sm font-medium text-text-secondary">Account Type:</span>
                <p className="text-text-primary capitalize">{draft.trading.accountType.replace(/-/g, ' ')}</p>
              </div>
              {draft.trading.accountType === 'prop-firm' && (
                <>
                  <div>
                    <span className="text-sm font-medium text-text-secondary">Prop Firm:</span>
                    <p className="text-text-primary">{draft.trading.propFirm}</p>
                  </div>
                  <div>
                    <span className="text-sm font-medium text-text-secondary">Prop Firm Status:</span>
                    <p className="text-text-primary capitalize">{draft.trading.propFirmStatus}</p>
                  </div>
                </>
              )}
              <div>
                <span className="text-sm font-medium text-text-secondary">Broker:</span>
                <p className="text-text-primary">{draft.trading.broker}</p>
              </div>
              <div>
                <span className="text-sm font-medium text-text-secondary">Trading Sessions:</span>
                <p className="text-text-primary">
                  {formatTradingSessions(draft.trading.tradingSessions, draft.trading.tradingSessionsOther)}
                </p>
              </div>
              <div className="md:col-span-2">
                <span className="text-sm font-medium text-text-secondary">Current Journaling Method:</span>
                <p className="text-text-primary">{draft.trading.currentJournaling}</p>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Beta Commitment Review */}
        <Card shadow="sm" className="border border-secondary">
          <CardHeader className="bg-secondary">
            <h3 className="text-lg font-semibold text-text-inverse">Beta Commitment</h3>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <div>
                <span className="text-sm font-medium text-text-secondary">Interest in {Brand.displayName} Journaling Features:</span>
                <p className="text-text-primary mt-2">{draft.commitment.stjInterest}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="text-sm font-medium text-text-secondary">Testing Commitment:</span>
                  <p className="text-text-primary">
                    {draft.commitment.testingCommitment === 'yes-committed'
                      ? 'Yes, I can commit to regular testing'
                      : draft.commitment.testingCommitment === 'maybe-limited'
                      ? 'Maybe, depending on my schedule'
                      : 'No, I have limited availability'
                    }
                  </p>
                </div>
                <div>
                  <span className="text-sm font-medium text-text-secondary">Feedback Commitment:</span>
                  <p className="text-text-primary">
                    {draft.commitment.feedbackCommitment === 'yes-committed'
                      ? 'Yes, I\'m committed to providing regular feedback'
                      : draft.commitment.feedbackCommitment === 'yes-limited'
                      ? 'Yes, but my feedback may be limited'
                      : 'No, I prefer minimal feedback requirements'
                    }
                  </p>
                </div>
              </div>
            </div>
          </CardBody>
        </Card>
      </CardBody>
    </Card>
  );
};